import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Tags, 
  Target, 
  AlertCircle, 
  CheckCircle2, 
  Loader2, 
  Eye, 
  Plus, 
  Minus,
  BookOpen
} from "lucide-react";

interface BulkLabelManagerProps {
  className?: string;
}

interface FilterOptions {
  topicIds: string[];
  difficulties: number[];
  tags: string[];
}

interface PreviewResult {
  previewCount: number;
  sampleMcqs: Array<{ id: string }>;
}

const BulkLabelManager = ({ className }: BulkLabelManagerProps) => {
  const [selectedLabelId, setSelectedLabelId] = useState<string>("");
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    topicIds: [],
    difficulties: [],
    tags: []
  });
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch exam labels
  const { data: examLabels, isLoading: labelsLoading } = useQuery({
    queryKey: ['exam-labels'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exam_labels')
        .select('id, name, slug, description')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  // Fetch topics for filtering
  const { data: topics } = useQuery({
    queryKey: ['topics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('topics')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data || [];
    }
  });

  // Fetch unique tags for filtering
  const { data: allTags } = useQuery({
    queryKey: ['mcq-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mcqs')
        .select('tags')
        .not('tags', 'is', null);
      if (error) throw error;
      
      const tagSet = new Set<string>();
      data?.forEach(mcq => {
        if (mcq.tags) {
          mcq.tags.forEach(tag => tagSet.add(tag));
        }
      });
      
      return Array.from(tagSet).sort();
    }
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('bulk-label-mcqs', {
        body: {
          labelId: selectedLabelId,
          operation: 'preview',
          filterOptions
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setPreviewResult(data);
      toast({
        title: "Preview Generated",
        description: `Found ${data.previewCount} MCQs matching your criteria`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Preview Failed",
        description: error.message || "Failed to generate preview",
        variant: "destructive",
      });
    }
  });

  // Apply labels mutation
  const applyMutation = useMutation({
    mutationFn: async () => {
      setIsProcessing(true);
      const { data, error } = await supabase.functions.invoke('bulk-label-mcqs', {
        body: {
          labelId: selectedLabelId,
          operation: 'apply',
          filterOptions
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setIsProcessing(false);
      setPreviewResult(null);
      queryClient.invalidateQueries({ queryKey: ['mcq-exam-labels'] });
      toast({
        title: "Labels Applied Successfully",
        description: data.message || `Labeled ${data.processedCount} MCQs`,
      });
    },
    onError: (error: any) => {
      setIsProcessing(false);
      toast({
        title: "Labeling Failed",
        description: error.message || "Failed to apply labels",
        variant: "destructive",
      });
    }
  });

  // Remove labels mutation
  const removeMutation = useMutation({
    mutationFn: async () => {
      setIsProcessing(true);
      const { data, error } = await supabase.functions.invoke('bulk-label-mcqs', {
        body: {
          labelId: selectedLabelId,
          operation: 'remove',
          filterOptions
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setIsProcessing(false);
      setPreviewResult(null);
      queryClient.invalidateQueries({ queryKey: ['mcq-exam-labels'] });
      toast({
        title: "Labels Removed Successfully",
        description: data.message || `Removed labels from ${data.removedCount} MCQs`,
      });
    },
    onError: (error: any) => {
      setIsProcessing(false);
      toast({
        title: "Label Removal Failed",
        description: error.message || "Failed to remove labels",
        variant: "destructive",
      });
    }
  });

  const handlePreview = () => {
    if (!selectedLabelId) {
      toast({
        title: "Label Required",
        description: "Please select an exam label first",
        variant: "destructive",
      });
      return;
    }
    previewMutation.mutate();
  };

  const handleApplyLabels = () => {
    if (!previewResult || !selectedLabelId) return;
    applyMutation.mutate();
  };

  const handleRemoveLabels = () => {
    if (!previewResult || !selectedLabelId) return;
    removeMutation.mutate();
  };

  const isAnyFilterActive = filterOptions.topicIds.length > 0 || 
                           filterOptions.difficulties.length > 0 || 
                           filterOptions.tags.length > 0;

  if (labelsLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tags className="h-5 w-5" />
          Bulk MCQ Labeling
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Label Selection */}
        <div className="space-y-2">
          <Label htmlFor="exam-label">Select Exam Label</Label>
          <Select value={selectedLabelId} onValueChange={setSelectedLabelId}>
            <SelectTrigger>
              <SelectValue placeholder="Choose an exam label..." />
            </SelectTrigger>
            <SelectContent>
              {examLabels?.map((label) => (
                <SelectItem key={label.id} value={label.id}>
                  <div className="flex flex-col">
                    <span className="font-medium">{label.name}</span>
                    <span className="text-xs text-muted-foreground">{label.slug}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Filters Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <Label className="font-medium">Filter MCQs (optional)</Label>
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Topic Multi-Select */}
            <div className="space-y-2">
              <Label>Topics</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    {filterOptions.topicIds.length > 0
                      ? `${filterOptions.topicIds.length} topic(s) selected`
                      : "Select topics..."
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search topics..." />
                    <CommandEmpty>No topics found.</CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-auto">
                      {topics?.map((topic) => (
                        <CommandItem
                          key={topic.id}
                          onSelect={() => {
                            const isSelected = filterOptions.topicIds.includes(topic.id);
                            setFilterOptions(prev => ({
                              ...prev,
                              topicIds: isSelected 
                                ? prev.topicIds.filter(id => id !== topic.id)
                                : [...prev.topicIds, topic.id]
                            }));
                          }}
                        >
                          <Checkbox 
                            checked={filterOptions.topicIds.includes(topic.id)}
                            className="mr-2"
                          />
                          {topic.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Difficulty Multi-Select */}
            <div className="space-y-2">
              <Label>Difficulty Levels</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    {filterOptions.difficulties.length > 0
                      ? `${filterOptions.difficulties.length} level(s) selected`
                      : "Select difficulties..."
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandEmpty>No difficulty levels found.</CommandEmpty>
                    <CommandGroup>
                      {[1, 2, 3, 4, 5].map((difficulty) => (
                        <CommandItem
                          key={difficulty}
                          onSelect={() => {
                            const isSelected = filterOptions.difficulties.includes(difficulty);
                            setFilterOptions(prev => ({
                              ...prev,
                              difficulties: isSelected 
                                ? prev.difficulties.filter(d => d !== difficulty)
                                : [...prev.difficulties, difficulty]
                            }));
                          }}
                        >
                          <Checkbox 
                            checked={filterOptions.difficulties.includes(difficulty)}
                            className="mr-2"
                          />
                          Level {difficulty} {difficulty === 1 ? "(Easy)" : difficulty === 3 ? "(Medium)" : difficulty === 5 ? "(Hard)" : ""}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Tag Multi-Select */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    {filterOptions.tags.length > 0
                      ? `${filterOptions.tags.length} tag(s) selected`
                      : "Select tags..."
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search tags..." />
                    <CommandEmpty>No tags found.</CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-auto">
                      {allTags?.map((tag) => (
                        <CommandItem
                          key={tag}
                          onSelect={() => {
                            const isSelected = filterOptions.tags.includes(tag);
                            setFilterOptions(prev => ({
                              ...prev,
                              tags: isSelected 
                                ? prev.tags.filter(t => t !== tag)
                                : [...prev.tags, tag]
                            }));
                          }}
                        >
                          <Checkbox 
                            checked={filterOptions.tags.includes(tag)}
                            className="mr-2"
                          />
                          {tag}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          {/* Selected Filters Display */}
          {isAnyFilterActive && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Active Filters:</Label>
              <div className="flex flex-wrap gap-2">
                {filterOptions.topicIds.map(topicId => {
                  const topic = topics?.find(t => t.id === topicId);
                  return topic ? (
                    <Badge key={topicId} variant="secondary" className="text-xs">
                      Topic: {topic.name}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-1 h-4 w-4 p-0 hover:bg-transparent"
                        onClick={() => setFilterOptions(prev => ({
                          ...prev,
                          topicIds: prev.topicIds.filter(id => id !== topicId)
                        }))}
                      >
                        ×
                      </Button>
                    </Badge>
                  ) : null;
                })}
                {filterOptions.difficulties.map(difficulty => (
                  <Badge key={difficulty} variant="secondary" className="text-xs">
                    Level {difficulty}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => setFilterOptions(prev => ({
                        ...prev,
                        difficulties: prev.difficulties.filter(d => d !== difficulty)
                      }))}
                    >
                      ×
                    </Button>
                  </Badge>
                ))}
                {filterOptions.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => setFilterOptions(prev => ({
                        ...prev,
                        tags: prev.tags.filter(t => t !== tag)
                      }))}
                    >
                      ×
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {isAnyFilterActive && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Filters are active. Only MCQs matching your criteria will be labeled.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        {/* Preview Section */}
        <div className="space-y-4">
          <Button
            onClick={handlePreview}
            disabled={!selectedLabelId || previewMutation.isPending}
            variant="outline"
            className="w-full"
          >
            <Eye className="h-4 w-4 mr-2" />
            {previewMutation.isPending ? 'Generating Preview...' : 'Preview MCQs'}
          </Button>

          {previewResult && (
            <Alert>
              <BookOpen className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <div className="font-medium">
                    Ready to label {previewResult.previewCount} MCQs
                  </div>
                  {!isAnyFilterActive && (
                    <div className="text-sm text-muted-foreground">
                      This will apply the label to ALL MCQs in the database.
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Action Buttons */}
        {previewResult && (
          <div className="space-y-3">
            <Separator />
            
            {isProcessing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing bulk operation...
                </div>
                <Progress value={undefined} className="h-2" />
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                onClick={handleApplyLabels}
                disabled={isProcessing || applyMutation.isPending}
                className="flex-1"
              >
                <Plus className="h-4 w-4 mr-2" />
                Apply Labels
              </Button>
              
              <Button
                onClick={handleRemoveLabels}
                disabled={isProcessing || removeMutation.isPending}
                variant="destructive"
                className="flex-1"
              >
                <Minus className="h-4 w-4 mr-2" />
                Remove Labels
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkLabelManager;